/**
 * ExposeLinks — Team-Verwaltung aller Exposé-Magic-Links.
 * Pattern-konsistent mit /dokumente-Akkordion und /investoren-Akkordion:
 * KPI-Strip oben, Filter-/Suchleiste, dann Tabelle mit Aktionen.
 *
 * Welle 8 (07/2026): Archiv-Toggle.
 * - „Aktive Links" (Default): nur nicht-archivierte
 * - „Archiv": nur archivierte Links mit Wiederherstellen/Endgültig-löschen
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Share2, Copy, Mail, XCircle, Plus, RefreshCw, User as UserIcon, Users,
  Calendar, Eye, Search, ExternalLink, Clock, Archive, ArchiveRestore, Trash2, Globe,
  FileDown, Loader2, AlertTriangle,
} from "lucide-react";
import {
  listExposeLinks, createExposeLink, patchExposeLink, deleteExposeLink,
  exposePublicUrl, archiveExposeLink, unarchiveExposeLink,
  generateExposePdf, exposePdfDownloadUrl, getExposePdfStatus,
} from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { fmtDate } from "../lib/format";

const STATUS_META = {
  aktiv:       { color: "emerald", label: "Aktiv" },
  abgelaufen:  { color: "gold",    label: "Abgelaufen" },
  widerrufen:  { color: "rose",    label: "Widerrufen" },
  archiviert:  { color: "navy",    label: "Archiviert" },
};

const STATUS_CLS = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  gold:    "bg-gold/10 text-[#8B6914] border-gold/40",
  rose:    "bg-rose-50 text-rose-800 border-rose-200",
  navy:    "bg-navy/8 text-navy border-navy/20",
  sky:     "bg-sky-50 text-sky-800 border-sky-200",
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.aktiv;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-sans border ${STATUS_CLS[meta.color]}`}>
      {meta.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const color = type === "master" ? "gold" : "sky";
  const Icon = type === "master" ? Users : UserIcon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-sans border ${STATUS_CLS[color]}`}>
      <Icon size={10} /> {type === "master" ? "Master" : "Investor"}
    </span>
  );
}

function VariantBadge({ variant, language }) {
  const isTeaser = variant === "teaser";
  return (
    <div className="flex items-center gap-1">
      <span
        className={`inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-sans border ${
          isTeaser
            ? "bg-heiss/10 text-heiss border-heiss/30"
            : "bg-navy/8 text-navy border-navy/20"
        }`}
        title={isTeaser ? "Teaser · für Cold Outreach" : "Vollversion · nach NDA"}
      >
        {isTeaser ? "Teaser" : "Vollversion"}
      </span>
      <span
        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-sans text-navy/55"
        title={language === "en" ? "Englisch" : "Deutsch"}
      >
        <Globe size={9} />
        {(language || "de").toUpperCase()}
      </span>
    </div>
  );
}

export default function ExposeLinks() {
  const [data, setData] = useState({ items: [], kpi: null });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("aktiv");           // "aktiv" | "archiv"
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);            // null | "create-master" | { kind: "archive", link }
  const [archiveTarget, setArchiveTarget] = useState(null); // link doc for archive confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listExposeLinks({
        link_type: filterType || undefined,
        archived: view === "archiv" ? true : undefined,
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Exposé-Links nicht laden");
    } finally {
      setLoading(false);
    }
  }, [filterType, view]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let items = data.items || [];
    if (view === "aktiv" && filterStatus) items = items.filter((i) => i.status === filterStatus);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      items = items.filter((i) =>
        (i.label || "").toLowerCase().includes(s) ||
        (i.investor_firma || "").toLowerCase().includes(s) ||
        (i.created_by || "").toLowerCase().includes(s));
    }
    return items;
  }, [data.items, filterStatus, search, view]);

  const copyLink = async (url) => {
    const full = url.startsWith("http") ? url : exposePublicUrl(url.replace("/expose/", ""));
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Link in Zwischenablage kopiert");
    } catch {
      window.prompt("Link kopieren:", full);
    }
  };

  const buildMailto = (link) => {
    const url = link.url.startsWith("http") ? link.url : exposePublicUrl(link.token);
    const lang = (link.language || "de");
    let subject, body;
    if (lang === "en") {
      subject = encodeURIComponent("Newton Campus Garching — Investment Opportunity");
      body = encodeURIComponent(
        `Dear Sir or Madam,\n\nplease find the ${link.variant === "teaser" ? "teaser" : "full exposé"} for our sale mandate Newton Campus Garching:\n\n${url}\n\nFor any questions, I remain at your disposal.\n\nKind regards`
      );
    } else {
      subject = encodeURIComponent("Newton Campus Garching — Exposé");
      body = encodeURIComponent(
        `Sehr geehrte Damen und Herren,\n\nanbei ${link.variant === "teaser" ? "der Teaser" : "das Exposé"} zu unserem Verkaufsmandat Newton Campus Garching:\n\n${url}\n\nBei Rückfragen stehe ich gerne zur Verfügung.\n\nMit freundlichen Grüßen`
      );
    }
    return `mailto:?subject=${subject}&body=${body}`;
  };

  const doRevoke = async (link) => {
    if (!window.confirm(`Link "${link.label || link.investor_firma}" wirklich widerrufen? Empfänger sehen dann sofort eine Fehlerseite. (Link bleibt in der aktiven Liste sichtbar — für endgültiges Ausblenden bitte „Archivieren".)`)) return;
    try {
      await deleteExposeLink(link.id);
      toast.success("Link widerrufen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Widerruf fehlgeschlagen");
    }
  };

  const doExtend = async (link) => {
    const days = window.prompt("Um wie viele Tage verlängern? (0 = kein Ablauf, ansonsten Zahl)", "30");
    if (days === null) return;
    const n = parseInt(days, 10);
    if (isNaN(n) || n < 0) { toast.error("Ungültige Zahl"); return; }
    try {
      const newExpiry = n === 0 ? "" : new Date(Date.now() + n * 86400000).toISOString();
      await patchExposeLink(link.id, { expires_at: newExpiry, is_active: true });
      toast.success(n === 0 ? "Ablauf entfernt · Link aktiv" : `Link um ${n} Tage verlängert`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verlängerung fehlgeschlagen");
    }
  };

  const doUnarchive = async (link) => {
    if (!window.confirm(`Link "${link.label || link.investor_firma}" wiederherstellen? Der Link erscheint dann wieder in der aktiven Liste — bleibt aber deaktiviert. Um ihn erneut nutzbar zu machen, danach auf „Verlängern / Reaktivieren" klicken.`)) return;
    try {
      await unarchiveExposeLink(link.id);
      toast.success("Link wiederhergestellt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    }
  };

  const doHardDelete = async (link) => {
    const label = link.label || link.investor_firma || link.token.slice(0, 8);
    const inp = window.prompt(
      `Diesen Link ENDGÜLTIG löschen? Diese Aktion kann nicht rückgängig gemacht werden.\n\nZur Bestätigung bitte "${label}" tippen:`
    );
    if (inp === null) return;
    if (inp.trim() !== label) {
      toast.error("Name stimmt nicht überein — Löschen abgebrochen");
      return;
    }
    try {
      await deleteExposeLink(link.id, { hard: true });
      toast.success("Link endgültig gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Endgültiges Löschen fehlgeschlagen");
    }
  };

  // Welle 9.4: Server-generiertes PDF (professionelles Investment-Memorandum).
  // Layer A: PDF ist meist via Pregeneration bereits im Cache → instant download.
  // Layer B: Wenn pdf_status=pending → Polling im useEffect + synchroner Fallback bei Klick.
  const [pdfLoading, setPdfLoading] = useState({});   // { [linkId]: true }

  // Poll alle 3s für Links mit pdf_status = pending
  useEffect(() => {
    const pendings = (data.items || []).filter((l) => l.pdf_status === "pending" && !l.archived_at);
    if (pendings.length === 0) return undefined;
    const iv = setInterval(async () => {
      try {
        const results = await Promise.all(pendings.map((l) => getExposePdfStatus(l.id).catch(() => null)));
        const anyChanged = results.some((r) => r && r.pdf_status && r.pdf_status !== "pending");
        if (anyChanged) load();
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [data.items, load]);

  const doDownloadPdf = async (link) => {
    // Wenn schon ready → direkter Download ohne Rendering
    if (link.pdf_status === "ready") {
      const url = exposePdfDownloadUrl(link.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Adldinger_Newton_Campus_Garching_${link.variant === "teaser" ? "Teaser" : "Expose"}_${(link.language || "de").toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      toast.success("PDF-Download gestartet");
      setTimeout(() => load(), 1500);
      return;
    }
    // Sonst: synchroner Fallback (generate-pdf ruft render_pdf on-demand)
    setPdfLoading((s) => ({ ...s, [link.id]: true }));
    toast.loading(
      link.variant === "teaser" ? "PDF wird erstellt · ~5 Sek" : "PDF wird erstellt · ~10 Sek",
      { id: `pdf-${link.id}` }
    );
    try {
      const meta = await generateExposePdf(link.id);
      const sizeMb = (meta.size_bytes / 1_000_000).toFixed(1);
      toast.success(
        meta.cached ? `PDF bereit (${sizeMb} MB · zwischengespeichert)` : `PDF erstellt (${sizeMb} MB · ${meta.duration_s} s)`,
        { id: `pdf-${link.id}` }
      );
      const url = exposePdfDownloadUrl(link.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta.filename || "Adldinger_Newton_Campus_Garching_Expose.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erstellung fehlgeschlagen", { id: `pdf-${link.id}` });
    } finally {
      setPdfLoading((s) => { const c = { ...s }; delete c[link.id]; return c; });
    }
  };

  const kpi = data.kpi;
  const isArchivView = view === "archiv";

  return (
    <div data-testid="expose-links-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      <PageHeader
        eyebrow="Newton Campus · Vertriebsmaterial"
        title="Exposé-Links"
        subtitle="Magic-Links zum PDF-Exposé pro Investor + Master-Links für offene Anlässe."
        testid="expose-header"
        actions={
          <>
            <button
              data-testid="expose-refresh-btn"
              onClick={load}
              className="pb-btn-ghost text-xs uppercase tracking-widish min-h-[40px]"
            >
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <button
              data-testid="expose-create-master-btn"
              onClick={() => setModal("create-master")}
              className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px]"
            >
              <Plus size={13} /> Master-Link
            </button>
          </>
        }
      />

      {/* View-Toggle Aktiv / Archiv */}
      <section
        data-testid="expose-view-toggle"
        className="inline-flex bg-white border border-navy/15 p-0.5"
      >
        <button
          data-testid="expose-view-aktiv"
          onClick={() => setView("aktiv")}
          className={`px-4 py-2 text-xs uppercase tracking-widish font-sans inline-flex items-center gap-2 transition-colors ${
            view === "aktiv"
              ? "bg-navy text-white font-medium"
              : "text-navy/60 hover:text-navy hover:bg-navy/5"
          }`}
        >
          <Share2 size={12} /> Aktive Links
          {!isArchivView && data.items?.length !== undefined && (
            <span className="tabular-nums opacity-70">({data.items.length})</span>
          )}
        </button>
        <button
          data-testid="expose-view-archiv"
          onClick={() => setView("archiv")}
          className={`px-4 py-2 text-xs uppercase tracking-widish font-sans inline-flex items-center gap-2 transition-colors ${
            view === "archiv"
              ? "bg-navy text-white font-medium"
              : "text-navy/60 hover:text-navy hover:bg-navy/5"
          }`}
        >
          <Archive size={12} /> Archiv
          {kpi?.archived !== undefined && kpi.archived > 0 && (
            <span className="tabular-nums opacity-70">({kpi.archived})</span>
          )}
        </button>
      </section>

      {/* KPI-Strip (nur Aktiv-View) */}
      {!isArchivView && kpi && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="expose-kpi">
          <div className="pb-kpi">
            <div className="pb-eyebrow"><Share2 size={11} className="inline mr-1" /> Aktive Links</div>
            <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{kpi.active}</div>
            <div className="text-[10px] text-navy/55 mt-1 font-sans">{kpi.total} insgesamt</div>
          </div>
          <div className="pb-kpi">
            <div className="pb-eyebrow"><Eye size={11} className="inline mr-1" /> Zugriffe gesamt</div>
            <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{kpi.access_total}</div>
            <div className="text-[10px] text-navy/55 mt-1 font-sans">Alle Empfänger</div>
          </div>
          <div className="pb-kpi">
            <div className="pb-eyebrow"><Clock size={11} className="inline mr-1" /> Letzte 7 Tage</div>
            <div className="text-2xl font-display font-bold text-heiss mt-1 tabular-nums">{kpi.access_7d}</div>
            <div className="text-[10px] text-navy/55 mt-1 font-sans">Zugriffe / Woche</div>
          </div>
          <div className="pb-kpi">
            <div className="pb-eyebrow"><Calendar size={11} className="inline mr-1" /> Nächster Ablauf</div>
            <div className="text-base font-display font-semibold text-navy mt-1">
              {kpi.next_expiry ? fmtDate(kpi.next_expiry) : "–"}
            </div>
            <div className="text-[10px] text-navy/55 mt-1 font-sans">Aktive Links</div>
          </div>
        </section>
      )}

      {/* Archiv-Info-Banner */}
      {isArchivView && (
        <section
          data-testid="expose-archiv-banner"
          className="border-l-2 border-navy bg-ledger px-4 py-3 flex items-start gap-3 text-sm font-sans"
        >
          <Archive size={16} className="text-navy shrink-0 mt-0.5" />
          <div>
            <div className="pb-eyebrow text-navy mb-1">Archiv</div>
            <p className="text-navy/80">
              Archivierte Links sind für Empfänger nicht mehr aufrufbar. Sie können sie
              <strong className="text-navy"> wiederherstellen</strong> oder <strong className="text-navy">endgültig löschen</strong>.
            </p>
          </div>
        </section>
      )}

      {/* Filter-Bar */}
      <section className="bg-white border border-navy/15 px-3 py-3 flex flex-wrap items-center gap-2" data-testid="expose-filterbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="expose-search"
            type="text"
            placeholder="Label, Firma oder Ersteller suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pb-input--full pl-9"
          />
        </div>
        <select
          data-testid="expose-filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="pb-input--full w-auto text-sm"
        >
          <option value="">Alle Typen</option>
          <option value="master">Nur Master-Links</option>
          <option value="investor">Nur Investor-Links</option>
        </select>
        {!isArchivView && (
          <select
            data-testid="expose-filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="pb-input--full w-auto text-sm"
          >
            <option value="">Alle Status</option>
            <option value="aktiv">Nur aktive</option>
            <option value="abgelaufen">Nur abgelaufene</option>
            <option value="widerrufen">Nur widerrufene</option>
          </select>
        )}
      </section>

      {/* Tabelle */}
      <section className="bg-white border border-navy/15" data-testid="expose-table">
        <div className="hidden md:grid grid-cols-[100px_1fr_130px_130px_130px_100px_120px_260px] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider font-sans text-navy/55 border-b border-navy/12 bg-ledger">
          <span>Typ</span>
          <span>Label / Investor</span>
          <span>Variante</span>
          <span>Erstellt</span>
          <span>{isArchivView ? "Archiviert" : "Läuft ab"}</span>
          <span className="tabular-nums">Zugriffe</span>
          <span>Status</span>
          <span className="text-right">Aktion</span>
        </div>
        {loading && (
          <div className="px-4 py-6 text-sm text-navy/55 font-sans text-center">Lädt…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-6 py-14 text-center font-sans">
            <Share2 size={28} className="text-navy/25 mx-auto mb-2" />
            <div className="text-sm text-navy/60">
              {isArchivView
                ? "Das Archiv ist leer."
                : "Noch keine Exposé-Links erzeugt."}
            </div>
            <div className="text-xs text-navy/45 mt-1">
              {isArchivView
                ? "Archivierte Links erscheinen hier, sobald Sie einen Link aus der aktiven Liste archivieren."
                : "Erzeuge einen Master-Link oder öffne einen Investor im Drawer."}
            </div>
          </div>
        )}
        <div className="divide-y divide-navy/8">
          {filtered.map((link) => (
            <div
              key={link.id}
              data-testid="expose-row"
              className="grid grid-cols-1 md:grid-cols-[100px_1fr_130px_130px_130px_100px_120px_260px] gap-2 md:gap-3 px-4 py-3 items-center hover:bg-navy/[0.02] transition-colors"
            >
              <div><TypeBadge type={link.link_type} /></div>
              <div className="min-w-0">
                <div className="font-sans text-sm text-navy font-medium truncate">
                  {link.label || link.investor_firma || "—"}
                </div>
                <div className="text-[11px] text-navy/50 font-sans truncate">
                  {link.link_type === "investor" && link.investor_id ? `Investor · ${link.investor_id.slice(0, 8)}…` : "Offener Zugang"}
                </div>
              </div>
              <div><VariantBadge variant={link.variant} language={link.language} /></div>
              <div className="text-xs text-navy/70 font-sans">
                {fmtDate(link.created_at)}<br />
                <span className="text-navy/45">von {link.created_by}</span>
              </div>
              <div className="text-xs text-navy/70 font-sans">
                {isArchivView ? (
                  <>
                    {link.archived_at ? fmtDate(link.archived_at) : "–"}
                    {link.archived_by && <><br /><span className="text-navy/45">von {link.archived_by}</span></>}
                    {link.archive_note && (
                      <div className="text-[10px] text-navy/60 italic mt-0.5 truncate" title={link.archive_note}>
                        „{link.archive_note}"
                      </div>
                    )}
                  </>
                ) : (
                  link.expires_at ? fmtDate(link.expires_at) : <span className="text-navy/40">kein Ablauf</span>
                )}
              </div>
              <div className="tabular-nums text-navy font-sans">
                <span className="text-base font-semibold">{link.access_count}</span>
                {link.last_access_at && (
                  <div className="text-[10px] text-navy/50">letzter: {fmtDate(link.last_access_at)}</div>
                )}
              </div>
              <div><StatusBadge status={link.status} /></div>
              <div className="flex items-center gap-1 justify-end flex-wrap">
                <button
                  onClick={() => copyLink(exposePublicUrl(link.token))}
                  data-testid="expose-copy-btn"
                  title="Link kopieren"
                  className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                >
                  <Copy size={12} />
                </button>
                {!isArchivView && (
                  <>
                    <a
                      href={buildMailto(link)}
                      data-testid="expose-mailto-btn"
                      title="Per E-Mail versenden"
                      className="p-1.5 text-navy/60 hover:text-gold hover:bg-gold/10 border border-navy/15"
                    >
                      <Mail size={12} />
                    </a>
                    <a
                      href={exposePublicUrl(link.token)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="expose-preview-btn"
                      title="Vorschau öffnen"
                      className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                    >
                      <ExternalLink size={12} />
                    </a>
                    <button
                      onClick={() => doDownloadPdf(link)}
                      disabled={pdfLoading[link.id]}
                      data-testid="expose-pdf-btn"
                      data-pdf-status={link.pdf_status || "unknown"}
                      title={
                        link.pdf_status === "ready" ? `Professionelles PDF bereit (${((link.pdf_size_bytes || 0) / 1_000_000).toFixed(1)} MB) — jetzt herunterladen` :
                        link.pdf_status === "pending" ? "PDF wird im Hintergrund erstellt — Klick für synchronen Download" :
                        link.pdf_status === "failed" ? "PDF-Erstellung fehlgeschlagen — Klick für Neuversuch" :
                        "Als professionelles PDF herunterladen"
                      }
                      className={`p-1.5 border relative ${
                        link.pdf_status === "ready" ? "text-gold border-gold/40 hover:bg-gold/10" :
                        link.pdf_status === "failed" ? "text-tot border-tot/40 hover:bg-tot/10" :
                        "text-navy/60 border-navy/15 hover:text-gold hover:bg-gold/10"
                      } disabled:opacity-60 disabled:cursor-wait`}
                    >
                      {pdfLoading[link.id] ? (
                        <Loader2 size={12} className="animate-spin"/>
                      ) : link.pdf_status === "pending" ? (
                        <>
                          <FileDown size={12} />
                          <Loader2 size={8} className="absolute -bottom-0.5 -right-0.5 animate-spin text-navy/70 bg-white rounded-full"/>
                        </>
                      ) : link.pdf_status === "failed" ? (
                        <>
                          <FileDown size={12} />
                          <AlertTriangle size={8} className="absolute -bottom-0.5 -right-0.5 text-tot bg-white rounded-full"/>
                        </>
                      ) : (
                        <FileDown size={12} />
                      )}
                    </button>
                    {link.status !== "aktiv" ? (
                      <button
                        onClick={() => doExtend(link)}
                        data-testid="expose-reactivate-btn"
                        title="Verlängern / reaktivieren"
                        className="px-2 py-1 text-[10px] uppercase tracking-widish text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-300 font-sans"
                      >
                        Reaktivieren
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => doExtend(link)}
                          data-testid="expose-extend-btn"
                          title="Ablauf verlängern"
                          className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                        >
                          <RefreshCw size={12} />
                        </button>
                        <button
                          onClick={() => doRevoke(link)}
                          data-testid="expose-revoke-btn"
                          title="Widerrufen (Link bleibt in aktiver Liste)"
                          className="p-1.5 text-tot/70 hover:text-white hover:bg-tot border border-tot/30"
                        >
                          <XCircle size={12} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setArchiveTarget(link)}
                      data-testid="expose-archive-btn"
                      title="Archivieren"
                      className="p-1.5 text-navy/60 hover:text-white hover:bg-navy border border-navy/30"
                    >
                      <Archive size={12} />
                    </button>
                  </>
                )}
                {isArchivView && (
                  <>
                    <button
                      onClick={() => doUnarchive(link)}
                      data-testid="expose-unarchive-btn"
                      title="Wiederherstellen"
                      className="px-2 py-1 text-[10px] uppercase tracking-widish text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-300 font-sans inline-flex items-center gap-1"
                    >
                      <ArchiveRestore size={11} /> Wiederherstellen
                    </button>
                    <button
                      onClick={() => doHardDelete(link)}
                      data-testid="expose-hard-delete-btn"
                      title="Endgültig löschen"
                      className="p-1.5 text-tot/70 hover:text-white hover:bg-tot border border-tot/30"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {modal === "create-master" && (
        <CreateMasterLinkModal
          onClose={() => setModal(null)}
          onCreated={() => { setModal(null); load(); }}
        />
      )}

      {archiveTarget && (
        <ArchiveConfirmModal
          link={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onDone={() => { setArchiveTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function ArchiveConfirmModal({ link, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await archiveExposeLink(link.id, note.trim());
      toast.success("Link archiviert");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archivieren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-40" />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-50"
        data-testid="expose-archive-modal"
      >
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Link archivieren</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">
            {link.label || link.investor_firma || link.token.slice(0, 10)}
          </h3>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm font-sans">
          <p className="text-navy/80 leading-relaxed">
            Der Link verschwindet aus der aktiven Liste und ist für Empfänger nicht
            mehr aufrufbar. Er kann jederzeit aus dem Archiv wiederhergestellt oder
            endgültig gelöscht werden.
          </p>
          <div>
            <label className="pb-label">Notiz (optional)</label>
            <textarea
              data-testid="expose-archive-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='Warum wurde archiviert? z. B. „Empfänger hat abgesagt", „NDA-Prozess abgeschlossen"…'
              className="pb-input--full text-sm min-h-[72px]"
              autoFocus
            />
            <div className="text-[11px] text-navy/55 mt-1">
              Sichtbar im Archiv — hilft bei der späteren Nachvollziehbarkeit.
            </div>
          </div>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost" disabled={saving}>Abbrechen</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="expose-archive-submit"
            className="pb-btn-primary gap-2"
          >
            <Archive size={13} /> {saving ? "Archiviert…" : "Archivieren"}
          </button>
        </div>
      </div>
    </>
  );
}

function CreateMasterLinkModal({ onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [variant, setVariant] = useState("vollversion");
  const [language, setLanguage] = useState("de");
  const [expiresDays, setExpiresDays] = useState(90);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!label.trim()) { toast.error("Label ist Pflicht"); return; }
    setSaving(true);
    try {
      const link = await createExposeLink({
        link_type: "master",
        label: label.trim(),
        variant,
        language,
        expires_days: parseInt(expiresDays, 10),
      });
      toast.success("Master-Link erzeugt");
      try {
        await navigator.clipboard.writeText(exposePublicUrl(link.token));
        toast.info("Link in Zwischenablage kopiert");
      } catch { /* keine clipboard-API */ }
      onCreated?.(link);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Link nicht erzeugen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40" />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-50"
        data-testid="expose-master-modal"
      >
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Master-Link erzeugen</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">
            Offener Zugang zum Exposé
          </h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="pb-label">Label (interne Bezeichnung)</label>
            <input
              data-testid="expose-master-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z. B. Investorentag 05.06.2026"
              className="pb-input--full"
              autoFocus
            />
            <div className="text-[11px] text-navy/55 mt-1 font-sans">
              Nicht öffentlich sichtbar — nur im Team-Dashboard zur Zuordnung.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pb-label">Variante</label>
              <select
                data-testid="expose-master-variant"
                value={variant}
                onChange={(e) => setVariant(e.target.value)}
                className="pb-input--full"
              >
                <option value="vollversion">Vollversion (nach NDA)</option>
                <option value="teaser">Teaser (Cold Outreach)</option>
              </select>
            </div>
            <div>
              <label className="pb-label">Sprache</label>
              <select
                data-testid="expose-master-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="pb-input--full"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
          <div>
            <label className="pb-label">Ablauf (Tage)</label>
            <select
              data-testid="expose-master-expiry"
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
              className="pb-input--full"
            >
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
              <option value="60">60 Tage</option>
              <option value="90">90 Tage (Standard)</option>
              <option value="180">180 Tage</option>
              <option value="0">Kein Ablauf</option>
            </select>
          </div>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="expose-master-submit"
            className="pb-btn-primary"
          >
            {saving ? "Erzeugt…" : "Erzeugen & Kopieren"}
          </button>
        </div>
      </div>
    </>
  );
}
